import { murmur3_32, fmix32, type Key } from "./hashing";
import { type RNG, defaultRng, randInt } from "./rng";

export interface CuckooFilterOptions {
  /** Target number of items; the table is sized a little larger than this. */
  capacity: number;
  /** Fingerprints per bucket (4 is the classic choice). */
  bucketSize?: number;
  /** Bits per fingerprint. More bits → lower false-positive rate. */
  fingerprintBits?: number;
  /** Max evictions before an insert gives up and the filter is "full". */
  maxKicks?: number;
  seed?: number;
  rng?: RNG;
}

/**
 * A Cuckoo filter: like a Bloom filter it answers approximate membership with
 * no false negatives, but it also supports **deletion** and tends to use less
 * space than a Bloom filter at low false-positive rates.
 *
 * It stores a short *fingerprint* of each item in one of two candidate buckets,
 * chosen by partial-key cuckoo hashing:
 *   i1 = hash(item)          i2 = i1 XOR hash(fingerprint)
 * The XOR relationship is symmetric, so from either bucket and the fingerprint
 * alone we can compute the other candidate — which is what makes eviction work
 * without rehashing the original item.
 *
 * Caveat inherited from the design: only delete items you actually inserted.
 * Deleting an item you never added can remove a *different* item that happens
 * to share a fingerprint and bucket.
 */
export class CuckooFilter {
  readonly numBuckets: number;
  readonly bucketSize: number;
  readonly fingerprintBits: number;
  readonly maxKicks: number;
  readonly seed: number;
  /** buckets[b*bucketSize + slot]; 0 means empty. */
  readonly buckets: Uint32Array;
  private readonly fpMask: number;
  private readonly bucketMask: number;
  private readonly rng: RNG;
  count = 0;

  constructor(options: CuckooFilterOptions) {
    const {
      capacity,
      bucketSize = 4,
      fingerprintBits = 8,
      maxKicks = 500,
      seed = 0,
      rng = defaultRng,
    } = options;
    if (capacity <= 0) throw new RangeError("capacity must be positive");
    if (fingerprintBits < 4 || fingerprintBits > 24)
      throw new RangeError("fingerprintBits must be in [4, 24]");

    this.bucketSize = bucketSize;
    this.fingerprintBits = fingerprintBits;
    this.maxKicks = maxKicks;
    this.seed = seed;
    this.rng = rng;

    // Bucket count must be a power of two for the XOR alternate-index trick.
    const wanted = Math.ceil(capacity / bucketSize / 0.95);
    this.numBuckets = nextPow2(Math.max(1, wanted));
    this.bucketMask = this.numBuckets - 1;
    this.fpMask = 2 ** fingerprintBits - 1;
    this.buckets = new Uint32Array(this.numBuckets * bucketSize);
  }

  /** Build a filter for `capacity` items at a target false-positive rate. */
  static create(
    capacity: number,
    falsePositiveRate: number,
    options: Omit<CuckooFilterOptions, "capacity" | "fingerprintBits"> = {},
  ): CuckooFilter {
    const bucketSize = options.bucketSize ?? 4;
    // FPR ≈ 2·bucketSize / 2^fingerprintBits, so solve for the bits.
    const bits = Math.ceil(Math.log2((2 * bucketSize) / falsePositiveRate));
    const fingerprintBits = Math.min(24, Math.max(4, bits));
    return new CuckooFilter({ ...options, capacity, bucketSize, fingerprintBits });
  }

  private fingerprint(key: Key): number {
    const fp = murmur3_32(key, this.seed ^ 0x5bd1e995) & this.fpMask;
    return fp === 0 ? 1 : fp; // 0 is reserved for "empty"
  }

  private indexOf(key: Key): number {
    return murmur3_32(key, this.seed) & this.bucketMask;
  }

  private altIndex(index: number, fp: number): number {
    return (index ^ (fmix32(fp) & this.bucketMask)) & this.bucketMask;
  }

  private tryPut(bucket: number, fp: number): boolean {
    const base = bucket * this.bucketSize;
    for (let s = 0; s < this.bucketSize; s++) {
      if (this.buckets[base + s] === 0) {
        this.buckets[base + s] = fp;
        return true;
      }
    }
    return false;
  }

  private bucketHas(bucket: number, fp: number): boolean {
    const base = bucket * this.bucketSize;
    for (let s = 0; s < this.bucketSize; s++) {
      if (this.buckets[base + s] === fp) return true;
    }
    return false;
  }

  /** Insert an item. Returns false if the filter is too full to place it. */
  add(key: Key): boolean {
    const fp = this.fingerprint(key);
    const i1 = this.indexOf(key);
    const i2 = this.altIndex(i1, fp);
    if (this.tryPut(i1, fp) || this.tryPut(i2, fp)) {
      this.count++;
      return true;
    }

    // Both candidate buckets are full — start evicting.
    let bucket = this.rng() < 0.5 ? i1 : i2;
    let carry = fp;
    for (let n = 0; n < this.maxKicks; n++) {
      const base = bucket * this.bucketSize;
      const slot = randInt(this.rng, this.bucketSize);
      const evicted = this.buckets[base + slot];
      this.buckets[base + slot] = carry;
      carry = evicted;
      bucket = this.altIndex(bucket, carry);
      if (this.tryPut(bucket, carry)) {
        this.count++;
        return true;
      }
    }
    return false; // filter is effectively full
  }

  /** Approximate membership: false means definitely absent. */
  has(key: Key): boolean {
    const fp = this.fingerprint(key);
    const i1 = this.indexOf(key);
    if (this.bucketHas(i1, fp)) return true;
    return this.bucketHas(this.altIndex(i1, fp), fp);
  }

  /** Remove one copy of an item. Returns true if a fingerprint was removed. */
  delete(key: Key): boolean {
    const fp = this.fingerprint(key);
    const i1 = this.indexOf(key);
    if (this.removeFrom(i1, fp)) return true;
    return this.removeFrom(this.altIndex(i1, fp), fp);
  }

  private removeFrom(bucket: number, fp: number): boolean {
    const base = bucket * this.bucketSize;
    for (let s = 0; s < this.bucketSize; s++) {
      if (this.buckets[base + s] === fp) {
        this.buckets[base + s] = 0;
        this.count--;
        return true;
      }
    }
    return false;
  }

  /** Slots occupied out of total slots. */
  loadFactor(): number {
    return this.count / (this.numBuckets * this.bucketSize);
  }

  /** Read a bucket's fingerprints (0 = empty), for visualisation. */
  bucket(i: number): number[] {
    const base = i * this.bucketSize;
    return Array.from(this.buckets.subarray(base, base + this.bucketSize));
  }

  /** The two candidate bucket indices for a key — used to visualise a lookup. */
  candidateBuckets(key: Key): [number, number] {
    const fp = this.fingerprint(key);
    const i1 = this.indexOf(key);
    return [i1, this.altIndex(i1, fp)];
  }

  /** The fingerprint a key reduces to (nonzero) — used to visualise a lookup. */
  fingerprintOf(key: Key): number {
    return this.fingerprint(key);
  }

  /** Theoretical false-positive rate ≈ 2·bucketSize / 2^fingerprintBits. */
  expectedFalsePositiveRate(): number {
    return (2 * this.bucketSize) / 2 ** this.fingerprintBits;
  }
}

function nextPow2(x: number): number {
  if (x <= 1) return 1;
  return 1 << Math.ceil(Math.log2(x));
}
